import { IconCopy, IconRotateClockwise2, IconTrash } from '@tabler/icons-react';
import { OBJ } from '../constants';
import Hint from '../../../components/common/Hint';

/**
 * Drag handles for the selected object, drawn in page space (points × scale).
 * Handle ids are read back by PageView's pointer logic.
 */

export const HANDLES = [
    { id: 'nw', x: 0, y: 0, cursor: 'nwse-resize' },
    { id: 'n', x: 0.5, y: 0, cursor: 'ns-resize' },
    { id: 'ne', x: 1, y: 0, cursor: 'nesw-resize' },
    { id: 'e', x: 1, y: 0.5, cursor: 'ew-resize' },
    { id: 'se', x: 1, y: 1, cursor: 'nwse-resize' },
    { id: 's', x: 0.5, y: 1, cursor: 'ns-resize' },
    { id: 'sw', x: 0, y: 1, cursor: 'nesw-resize' },
    { id: 'w', x: 0, y: 0.5, cursor: 'ew-resize' },
];

export default function SelectionFrame({
    object,
    scale,
    onHandleDown,
    onRotateDown,
    onDelete,
    onDuplicate,
    labels,
    /** While typing, keep the resize handles but drop rotate and the actions. */
    editing = false,
}) {
    if (!object) return null;

    const left = object.x * scale;
    const top = object.y * scale;
    const width = object.w * scale;
    const height = object.h * scale;
    const isLine = object.type === OBJ.LINE || object.type === OBJ.ARROW;
    // A text box's height follows its text, so it has nothing for an edge
    // handle to do: its four corners set the width (PageView ignores their
    // vertical travel) and the paragraph re-wraps to fit.
    const cornersOnly = isLine || object.type === OBJ.TEXT;
    const handles = cornersOnly
        ? HANDLES.filter((h) => ['nw', 'ne', 'se', 'sw'].includes(h.id))
        : HANDLES;

    return (
        <div
            className="pointer-events-none absolute"
            style={{
                left,
                top,
                width,
                height,
                transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
                transformOrigin: 'center',
            }}
        >
            {editing ? null : <div className="absolute inset-0 border border-primary" />}

            {handles.map((handle) => (
                <button
                    key={handle.id}
                    type="button"
                    aria-label={handle.id}
                    onPointerDown={(e) => onHandleDown(e, handle.id)}
                    className="pointer-events-auto absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-[3px] border border-primary bg-white shadow-sm"
                    style={{
                        left: `${handle.x * 100}%`,
                        top: `${handle.y * 100}%`,
                        cursor: handle.cursor,
                    }}
                />
            ))}

            {editing ? null : (
            <Hint label={labels.rotate} side="top">
                <button
                    type="button"
                    aria-label={labels.rotate}
                    onPointerDown={onRotateDown}
                    className="pointer-events-auto absolute left-1/2 flex h-6 w-6 -translate-x-1/2 cursor-grab items-center justify-center rounded-full border border-primary bg-white text-primary shadow-sm"
                    style={{ top: -30 }}
                >
                    <IconRotateClockwise2 size={13} stroke={2} />
                </button>
            </Hint>
            )}

            {editing ? null : (
            <div
                className="pointer-events-auto absolute right-0 flex items-center gap-1 rounded-lg border border-[#ECE8E4] bg-white px-1 py-1 shadow-[0_4px_12px_rgba(28,24,21,0.14)] dark:border-zinc-700 dark:bg-zinc-900"
                style={{ bottom: -38 }}
            >
                <Hint label={labels.duplicate}>
                    <button
                        type="button"
                        aria-label={labels.duplicate}
                        onClick={onDuplicate}
                        className="flex h-6 w-6 items-center justify-center rounded-md text-[#3D3833] hover:bg-[#F4F1ED] dark:text-zinc-200 dark:hover:bg-zinc-800"
                    >
                        <IconCopy size={14} stroke={1.8} />
                    </button>
                </Hint>
                <Hint label={labels.delete}>
                    <button
                        type="button"
                        aria-label={labels.delete}
                        onClick={onDelete}
                        className="flex h-6 w-6 items-center justify-center rounded-md text-[#D33A2C] hover:bg-[#FDECEA] dark:hover:bg-red-950/30"
                    >
                        <IconTrash size={14} stroke={1.8} />
                    </button>
                </Hint>
            </div>
            )}
        </div>
    );
}
